import { type FC, useEffect, useRef, useState } from 'react';

/**
 * Marker is the React component that displays the standard map marker on Map.
 *
 * Marker must be a child component of Map so that Map can clone itself into the
 * map property of Marker after the map has initialized.
 *
 * When nested under a `<SlabMarkerCluster>`, the marker registers itself with
 * the surrounding `MarkerClusterer` instead of attaching directly to the map,
 * so it can be grouped/ungrouped on zoom changes.
 */
import { hasValue } from '@core/utils/equality';

import { UseDeepCompareEffectForMaps } from './SlabMap';
import {
  type SlabMapMarkerClusterMeta,
  setSlabMapMarkerClusterMeta,
} from './slabMapMarkerClusterMeta';
import { useClustererContext } from './SlabMarkerCluster';

interface SlabMarkerProps extends google.maps.marker.AdvancedMarkerElementOptions {
  location?: google.maps.LatLngLiteral | google.maps.LatLng;
  onClick?(e: google.maps.MapMouseEvent): void;
  infoContent?: string | Element | Text | null | undefined;
  /** Used by cluster renderers to build multi-type cluster borders. */
  clusterMeta?: SlabMapMarkerClusterMeta;
}

export const SlabMarker: FC<SlabMarkerProps> = ({
  location,
  onClick,
  infoContent,
  clusterMeta,
  map,
  ...options
}) => {
  const [marker, setMarker] = useState<google.maps.marker.AdvancedMarkerElement | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow>();
  const clustererCtx = useClustererContext();

  UseDeepCompareEffectForMaps(() => {
    if (!hasValue(marker)) {
      return;
    }

    if (hasValue(infoContent) && !hasValue(infoWindowRef.current)) {
      infoWindowRef.current = new window.google.maps.InfoWindow({
        headerDisabled: true,
      });
    }

    if (hasValue(onClick)) {
      marker?.addListener('gmp-click', onClick);
    }

    if (hasValue(infoContent) && hasValue(infoWindowRef.current)) {
      marker.content?.addEventListener('mouseenter', () => {
        infoWindowRef.current?.setContent(infoContent);
        infoWindowRef.current?.open({
          anchor: marker,
          map: marker.map ?? clustererCtx?.map,
        });
      });

      marker.content?.addEventListener('mouseleave', () => {
        infoWindowRef.current?.close();
      });
    }
  }, [marker]);

  useEffect(() => {
    if (marker !== null) {
      if (hasValue(clustererCtx)) {
        // `noDraw` — the coalesced `scheduleDraw` below triggers a single redraw.
        clustererCtx.clusterer.removeMarker(marker, true);
      }
      marker.map = null;
    }

    const newMarker = new google.maps.marker.AdvancedMarkerElement({
      position: location,
      ...options,
      // When clustered, the clusterer owns `map` attachment — leave it null.
      map: hasValue(clustererCtx) ? null : map,
    });

    if (hasValue(clusterMeta)) {
      setSlabMapMarkerClusterMeta(newMarker, clusterMeta);
    }

    if (hasValue(clustererCtx)) {
      clustererCtx.clusterer.addMarker(newMarker, true);
      clustererCtx.scheduleDraw();
    }

    setMarker(newMarker);

    return (): void => {
      if (hasValue(clustererCtx)) {
        clustererCtx.clusterer.removeMarker(newMarker, true);
        clustererCtx.scheduleDraw();
      }
      newMarker.map = null;
    };
    // `clustererCtx` is stable for the lifetime of a `<SlabMarkerCluster>` so
    // we deliberately re-create the marker only on `location` / cluster meta changes.
  }, [location, clusterMeta?.locationType, clusterMeta?.color]);

  return null;
};
